import { useEffect, useState } from 'react';

// Re-renders the calling component on a fixed interval, off a single timer
// shared by every subscriber of that interval length. A list of live rows
// therefore costs one setInterval rather than one per row, and since every
// subscriber is notified inside the same task, React batches the whole list
// into one commit instead of one per row.
//
// IntervalRenderer does the same job per instance and is still used across the
// meeting, supervisor and call UIs; this hook is deliberately kept separate
// until it has proven itself here.

type Ticker = {
    intervalId: number;
    subscribers: Set<() => void>;
};

const tickers = new Map<number, Ticker>();

const subscribe = (interval: number, onTick: () => void) => {
    let ticker = tickers.get(interval);

    if (!ticker) {
        // The captured Set is the ticker's own and is mutated in place, so a
        // component subscribing or unsubscribing mid-tick is picked up.
        const subscribers = new Set<() => void>();
        ticker = {
            subscribers,
            intervalId: window.setInterval(
                () => subscribers.forEach((notify) => notify()),
                interval,
            ),
        };
        tickers.set(interval, ticker);
    }

    ticker.subscribers.add(onTick);

    return () => {
        const current = tickers.get(interval);
        if (!current) return;

        current.subscribers.delete(onTick);
        // The last subscriber takes the timer down with it, so an idle app
        // holds no timers at all.
        if (current.subscribers.size === 0) {
            clearInterval(current.intervalId);
            tickers.delete(interval);
        }
    };
};

// Pass null to stay subscribed to nothing — for a component that renders
// something static until it has a value worth counting. Returns the number of
// ticks so far, for a caller that wants to key off it; the re-render is the
// point, so ignoring it is the common case.
const useSharedInterval = (interval: number | null) => {
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (interval === null) return;

        return subscribe(interval, () => setTick((previous) => previous + 1));
    }, [interval]);

    return tick;
};

export default useSharedInterval;
